import * as React from 'react';
import MuiButton from '@mui/material/Button';
import MuiButtonGroup from '@mui/material/ButtonGroup';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import { CircularProgress, styled } from '@mui/material';
import Text from '@components/Text';

const ButtonGroup = styled(MuiButtonGroup)(({ theme }) => ({
    borderRadius: 10,
    boxShadow: 'none',
    overflow: 'hidden',
    '& .MuiButton-root': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.neutralWhite.main,
        textTransform: 'none',
        borderRadius: 0,
        boxShadow: 'none',
        minWidth: 'unset',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    '&.MuiButtonGroup-sizeMedium .MuiButton-root': {
        padding: '6px 16px',
    },
    '&.MuiButtonGroup-sizeSmall .MuiButton-root': {
        padding: '0px 10px',
        fontSize: '0.8125rem',
    },
}));

const Button = styled(MuiButton)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.neutralWhite.main,
    '&:hover': {
        backgroundColor: theme.palette.primary.dark,
    },
    '&:first-child': {
        borderRight: `solid 1px ${theme.palette.primary.dark}`,
        gap: 8,
    },
    '&:last-child': {
        minWidth: 40,
    },
}));

export enum ButtonSaveType {
    SAVE_AND_ADD = 'SAVE_AND_ADD',
    SAVE_AND_RETURN = 'SAVE_AND_RETURN',
}

const options = [ButtonSaveType.SAVE_AND_RETURN, ButtonSaveType.SAVE_AND_ADD];

type Props = {
    onClick?: (type: ButtonSaveType) => void;
    onChange?: (type: ButtonSaveType) => void;
    loading?: boolean;
    disabled?: boolean;
    size?: 'small' | 'medium' | 'large';
    labels?: {
        [key in ButtonSaveType]: string;
    };
};

const ButtonSaveSplit = (props: Props) => {
    const [open, setOpen] = React.useState(false);
    const anchorRef = React.useRef<HTMLDivElement>(null);
    const [selectedIndex, setSelectedIndex] = React.useState(0);

    const handleClick = () => {
        props.onClick?.(options[selectedIndex]);
    };

    const handleMenuItemClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>, index: number) => {
        setSelectedIndex(index);
        props.onChange?.(options[index]);
        setOpen(false);
    };

    const handleToggle = () => {
        setOpen(prevOpen => !prevOpen);
    };

    const handleClose = (event: Event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }

        setOpen(false);
    };

    return (
        <React.Fragment>
            <ButtonGroup variant="contained" ref={anchorRef} aria-label="split button" size={props.size}>
                <Button onClick={handleClick} variant="contained" size={props.size} disabled={props.disabled || props.loading}>
                    {props.loading ? (
                        <CircularProgress size={props.size === 'small' ? 14 : 18} sx={{ color: 'inherit' }} />
                    ) : (
                        <i className='fi fi-br-check' style={{ fontSize: props.size === 'small' ? 12 : 16, color: 'currentColor' }} />
                    )}
                    {props.labels[options[selectedIndex]]}
                </Button>
                <Button
                    variant="contained"
                    size={props.size}
                    disabled={props.disabled || props.loading}
                    aria-controls={open ? 'split-button-menu' : undefined}
                    aria-expanded={open ? 'true' : undefined}
                    aria-label="select merge strategy"
                    aria-haspopup="menu"
                    onClick={handleToggle}>
                    <i className='fi fi-br-angle-small-down' style={{ marginTop: props.size === 'small' ? 2 : 4, fontSize: props.size === 'small' ? 14 : 18, color: 'currentColor' }} />
                </Button>
            </ButtonGroup>
            <Popper
                sx={{
                    zIndex: 5,
                }}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal>
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                        }}>
                        <Paper sx={{ borderRadius: '10px' }}>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList id="split-button-menu" autoFocusItem>
                                    {options.map((option, index) => (
                                        <MenuItem
                                            style={{
                                                padding: '8px 24px',
                                            }}
                                            key={option}
                                            disabled={index === 2}
                                            selected={index === selectedIndex}
                                            onClick={event => handleMenuItemClick(event, index)}>
                                            <Text dark>{props.labels[option]}</Text>
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </React.Fragment>
    );
};

export default ButtonSaveSplit;
